// Direct pyst access. The active project's apiPath is the base URL; AuthInterceptor
// attaches the Keycloak bearer token.
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { AuthService } from './auth.service';
import { BridgeService } from './bridge.service';
import { normalizePystWhitespace } from './pyst-format';

/** A failed pyst request, carrying enough to render the error inside the frame. */
export class PystRequestError extends Error {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        public readonly body: string,
        public readonly url: string,
    ) {
        super(status ? `HTTP ${status} ${statusText}` : statusText);
    }
}

/**
 * Percent-decode only the characters that are pyst query syntax. Deliberately not a
 * full decodeURIComponent: `%20` and friends are left for the backend's own query
 * decoding, and a value containing a literal percent sign must survive untouched.
 */
export function decodePystSyntax(url: string): string {
    if (!url.includes('%')) {
        return url;
    }
    return url.replace(/%(24|28|29|2C|2c|27)/g, (match: string, code: string) => {
        switch (code.toUpperCase()) {
            case '24':
                return '$';
            case '28':
                return '(';
            case '29':
                return ')';
            case '2C':
                return ',';
            case '27':
                return "'";
            default:
                return match;
        }
    });
}

/**
 * Percent-encode `#`, which a URL treats as the start of the fragment.
 *
 * Everything after a literal `#` is stripped by the client and never sent, so a hex
 * colour such as `$color('#b3261e')` truncates the request mid-expression and pyst
 * reports an unterminated segment. pyst decodes `%23` back to `#` when it parses the
 * query, so encoding here is lossless.
 */
export function encodeUrlFragmentMarker(url: string): string {
    return url.replace(/#/g, '%23');
}

/**
 * The lifetime to ask for when a token should live as long as the server
 * allows: the server cuts it to its PYST_EMBED_TOKEN_MAX_TTL_SECONDS (a day
 * unless configured), so that setting alone decides.
 */
export const EMBED_TOKEN_LONGEST_TTL_SECONDS = 10 * 365 * 24 * 3600;

/** What `POST /embed-token` returns: the token, when it dies, and the URL to use. */
export interface EmbedToken {
    token: string;
    /** ISO timestamp of expiry. */
    expiresAt: string;
    /** The full query URL with the token appended; what a frame's `src` should be. */
    url: string;
}

@Injectable({ providedIn: 'root' })
export class PystService {
    constructor(
        private http: HttpClient,
        private bridges: BridgeService,
        private auth: AuthService,
    ) {}

    /**
     * Build the request URL.
     *
     * pyst's query language uses `$ ( ) , '` as syntax and reads them from the raw
     * request path, so they must arrive literal. Percent-encoded forms break macro
     * recognition: `~name(%24arg(1))` parses `%24arg(1)` as a positional argument and
     * the call is rejected with "expects 0 positional arguments".
     *
     * So: no encoding of our own (the dashboard's PystService concatenates plainly and
     * only normalises newlines), plus an explicit decode of those five characters in
     * case the query text already carries them encoded — from a URL pasted out of a
     * browser address bar, for instance.
     */
    url(path: string): string {
        const bridge = this.bridges.activeBridge;
        if (!bridge) {
            throw new Error('No project is active');
        }
        const base = bridge.apiPath.replace(/\/+$/, '');
        return encodeUrlFragmentMarker(normalizePystWhitespace(decodePystSyntax(base + this.relative(path))));
    }

    /**
     * The query as one canonical line relative to the API root — what the server
     * binds an embed token to. A stored query may be pretty-printed across lines;
     * collapse it (whitespace only, outside string literals) so the wire form is
     * the same however it was written.
     */
    relativeQuery(path: string): string {
        return encodeUrlFragmentMarker(normalizePystWhitespace(decodePystSyntax(this.relative(path))));
    }

    private relative(path: string): string {
        return path.startsWith('/') ? path : `/${path}`;
    }

    /**
     * Mint a short-lived token bound to one query, and the URL that carries it. A
     * frame in another application can load that URL with no other credential;
     * the token lets it run that one query, read-only, until it expires (the
     * server's default unless asked otherwise, never more than its maximum).
     */
    mintEmbedToken(path: string, ttlSeconds?: number): Promise<EmbedToken> {
        const body: { query: string; ttl_seconds?: number } = { query: this.relativeQuery(path) };
        if (ttlSeconds !== undefined && Number.isFinite(ttlSeconds) && ttlSeconds > 0) {
            body.ttl_seconds = Math.round(ttlSeconds);
        }
        return this.post<EmbedToken>('/embed-token', body);
    }

    get<T>(path: string): Promise<T> {
        return firstValueFrom(this.http.get<T>(this.url(path)));
    }

    post<T>(path: string, body: unknown): Promise<T> {
        return firstValueFrom(this.http.post<T>(this.url(path), body));
    }

    patch<T>(path: string, body: unknown): Promise<T> {
        return firstValueFrom(this.http.patch<T>(this.url(path), body));
    }

    delete<T>(path: string): Promise<T> {
        return firstValueFrom(this.http.delete<T>(this.url(path)));
    }

    /** pyst's own parser, for linting: success, or a message (no position is reported). */
    parse(query: string): Promise<{ success: boolean; message?: string }> {
        return firstValueFrom(
            this.http.post<{ success: boolean; message?: string }>(this.url('/parse'), { query }),
        );
    }

    /**
     * Fetch a py_plot response as text.
     *
     * Raw XHR rather than HttpClient, again matching the dashboard: HttpClient is a
     * layer between this code and the wire that can normalise the URL, and pyst needs
     * the path delivered verbatim. It also lets the Content-Type pyst omits be ignored
     * outright instead of negotiated.
     */
    getText(path: string): Promise<string> {
        return this.getRaw(path).then((response) => response.text);
    }

    /**
     * The response body with the content type pyst gave it.
     *
     * A plot arrives as `text/html` and anything else as JSON, which is what lets
     * one card show either without the query having to declare which it is.
     */
    getRaw(path: string): Promise<{ text: string; contentType: string }> {
        const url = this.url(path);
        const token = this.auth.getToken();

        return new Promise<{ text: string; contentType: string }>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url);
            if (token) {
                xhr.setRequestHeader('Authorization', 'Bearer ' + token);
            }
            xhr.responseType = 'text';
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve({
                        text: xhr.responseText,
                        contentType: (xhr.getResponseHeader('Content-Type') ?? '').toLowerCase(),
                    });
                } else {
                    reject(new PystRequestError(xhr.status, xhr.statusText, xhr.responseText, url));
                }
            };
            xhr.onerror = () => reject(new PystRequestError(0, 'Network error', '', url));
            xhr.send();
        });
    }
}
